import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface CourseListItem {
  id: number;
  name: string;
  courseShortCode: string;
  courseType: number | null;
  courseTypeName: string | null;
  streamId: number | null;
  streamName: string | null;
  isElective: number | null;
  enableStatus: number;
  listPrice: number | null;
  numberOfSessions: number | null;
  maxStudents: number | null;
  startDate: string | null;
  endDate: string | null;
  schools: string | null;
}

export interface CourseDetail {
  id: number;
  name: string;
  courseShortCode: string;
  courseType: number | null;
  streamId: number | null;
  isElective: number | null;
  enableStatus: number;
  listPrice: number | null;
  numberOfSessions: number | null;
  remarks: string | null;
  startDate: string | null;
  endDate: string | null;
  maxStudents: number | null;
  lectures: number | null;
  tutorials: number | null;
  practicals: number | null;
  internalCredit: number | null;
  externalCredit: number | null;
  localFee: number | null;
  internationalFee: number | null;
  sortOrder: number | null;
  parentId: number | null;
  schoolIds: number[];
  termIds: number[];
  programIds: number[];
}

export interface CourseSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  courseTypeId?: number;
  streamId?: number;
  schoolId?: number;
  programId?: number;
  termId?: number;
  yearId?: number;
  isElective?: string;
  includeInactive?: boolean;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface CourseFormData {
  name: string;
  courseShortCode: string;
  courseType: number | null;
  streamId: number | null;
  isElective: number | null;
  enableStatus?: number;
  listPrice: number | null;
  numberOfSessions: number | null;
  remarks: string | null;
  startDate: string | null;
  endDate: string | null;
  maxStudents: number | null;
  lectures: number | null;
  tutorials: number | null;
  practicals: number | null;
  internalCredit: number | null;
  externalCredit: number | null;
  localFee: number | null;
  internationalFee: number | null;
  sortOrder: number | null;
  schoolIds: number[];
  termIds: number[];
}

export interface CourseLookups {
  isProgramBased: boolean;
  courseTypes: { id: number; name: string }[];
  streams: { id: number; name: string }[];
  schools: { id: number; name: string }[];
  programs: { id: number; name: string; code: string | null }[];
  terms: { id: number; name: string; programId: number | null }[];
}

export interface CourseListResponse {
  success: boolean;
  data: {
    courses: CourseListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface CourseResponse {
  success: boolean;
  data?: CourseDetail;
  message?: string;
  error?: string;
}

export interface CourseDocument {
  mappingId: number;
  studyResourceId: number;
  Name: string;
  URL: string | null;
  TypeId: number | null;
  FileSizeInByte: number | null;
  MetaData: any;
  typeName: string | null;
  SortOrder: number;
  CreatedDate: string | null;
  ragStatus: string | null;
  chunksCreated: number | null;
  contentHash: string | null;
  ragUpdatedAt: string | null;
}

export interface CourseRagIndex {
  status: string;
  totalResources: number;
  processedResources: number;
  totalChunks: number;
  totalVectors: number;
  processingStartedAt: string | null;
  processingCompletedAt: string | null;
  errorSummary: string | null;
}

export interface CourseDocumentsResponse {
  success: boolean;
  data: {
    documents: CourseDocument[];
    ragIndex: CourseRagIndex | null;
  };
}

class CourseManagementService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/courses/manage`;
  }

  async searchCourses(params: CourseSearchParams): Promise<CourseListResponse> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/search`, { params });
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error searching courses:", error);
      return {
        success: false,
        data: {
          courses: [],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        },
      };
    }
  }

  async getCourseById(id: number): Promise<CourseResponse> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error fetching course:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch course",
      };
    }
  }

  async createCourse(data: CourseFormData): Promise<CourseResponse> {
    try {
      const response = await myAxios.post(this.baseUrl, data);
      return response.data as unknown as CourseResponse;
    } catch (error: any) {
      console.error("[CourseManagementService] Error creating course:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to create course",
      };
    }
  }

  async updateCourse(id: number, data: CourseFormData): Promise<CourseResponse> {
    try {
      const response = await myAxios.put(`${this.baseUrl}/${id}`, data);
      return response.data as unknown as CourseResponse;
    } catch (error: any) {
      console.error("[CourseManagementService] Error updating course:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update course",
      };
    }
  }

  async deleteCourse(id: number): Promise<CourseResponse> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error deleting course:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete course",
      };
    }
  }

  async getLookups(): Promise<{ success: boolean; data: CourseLookups }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/lookups`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error fetching lookups:", error);
      return {
        success: false,
        data: { isProgramBased: false, courseTypes: [], streams: [], schools: [], programs: [], terms: [] },
      };
    }
  }

  // ── Course Document Methods ──

  async getCourseDocuments(courseId: number): Promise<CourseDocumentsResponse> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${courseId}/documents`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error fetching documents:", error);
      return {
        success: false,
        data: { documents: [], ragIndex: null },
      };
    }
  }

  async uploadCourseDocument(courseId: number, file: File): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const response = await myAxios.post(`${this.baseUrl}/${courseId}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data as unknown as { success: boolean; message?: string; data?: any };
    } catch (error: any) {
      console.error("[CourseManagementService] Error uploading document:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload document",
      };
    }
  }

  async linkCourseDocument(courseId: number, studyResourceId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/${courseId}/documents/link`, { studyResourceId });
      return response.data as unknown as { success: boolean; message?: string };
    } catch (error: any) {
      console.error("[CourseManagementService] Error linking document:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to link document",
      };
    }
  }

  async removeCourseDocument(courseId: number, studyResourceId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/${courseId}/documents/${studyResourceId}`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error removing document:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to remove document",
      };
    }
  }

  async ragifyCourseDocuments(courseId: number): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/${courseId}/documents/ragify`);
      return response.data;
    } catch (error: any) {
      console.error("[CourseManagementService] Error ragifying documents:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to ragify documents",
      };
    }
  }
}

export default new CourseManagementService();
